import { useCallback, useMemo, useState } from "react";

import { Button, notification } from "antd";
import { HistoryOutlined } from "@ant-design/icons";
import ModalHistory from "../modal/history";
import { getHistory } from "../../../services/history";
import * as settings from "../../../services/settings";
import { isEmpty } from "../../../utils/common";
import { useTranslation } from "react-i18next";

const defaultTableData = {
  loading: false,
  dataSource: [],
  pagination: { current: 1, pageSize: 10, total: 0 },
};

const BtnHistory = ({ titleRef, object_id, content_type_id, company_bill_id, customer_bill_id, customer_id }) => {
  const { t } = useTranslation(["buttons", "history"]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tableData, setTableData] = useState(defaultTableData);
  const [api, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = (type, message, description) => {
    api[type]({ message, description });
  };

  const title = useMemo(() => {
    return t("history:Title_History", { title: titleRef });
  }, [titleRef, t]);

  const contentTypeIds = useMemo(() => {
    if (content_type_id == null) return [];
    return Array.isArray(content_type_id) ? content_type_id : [content_type_id];
  }, [content_type_id]);

  const hasHistoryScope = Boolean(
    company_bill_id ?? customer_bill_id ?? customer_id ?? object_id
  );

  const searchHistory = useCallback(
    async (pageLimit = {}) => {
      if (!hasHistoryScope) return;

      const page = pageLimit.page ?? 1;
      const limit = pageLimit.limit ?? 10;

      try {
        setTableData((prev) => ({ ...prev, loading: true }));

        const params = { limit, page };

        if (contentTypeIds.length > 0) {
          params.content_type_ids = contentTypeIds;
        }

        if (customer_bill_id != null) {
          params.customer_bill_id = customer_bill_id;
        } else if (company_bill_id != null) {
          params.company_bill_id = company_bill_id;
        } else if (customer_id != null) {
          params.customer_id = customer_id;
        } else if (object_id != null) {
          params.object_id = object_id;
        }

        const resp = await getHistory(params);

        const dataSource = (resp.query ?? []).map((item) => {
          const msgList = [];

          if (!isEmpty(item.change_message)) {
            const parsedMessage = JSON.parse(item.change_message);

            if (Array.isArray(parsedMessage)) {
              parsedMessage.forEach((log) => {
                const getFieldLabel = (name) => (name ? t(`history:fields.${name}`, { defaultValue: name }) : "");
                const buildChangedFromToMsg = (raw, fieldName) => {
                  const fieldLabel = getFieldLabel(fieldName);
                  const match = raw.match(/from\s+"([^"]*)"\s+to\s+"([^"]*)"/i);

                  if (match) {
                    const [, from, to] = match;
                    return t("history:changed_from_to", {
                      field: fieldLabel || fieldName,
                      from,
                      to,
                    });
                  }
                  return t("history:changed_detail", {
                    field: fieldLabel || fieldName,
                    detail: raw,
                  });
                };

                // ---------- ADDED ----------
                if (log.added) {
                  const obj = log.added;
                  const fieldLabel = getFieldLabel(obj.name);
                  const detail = obj.object;

                  if (!fieldLabel && !detail) {
                    msgList.push(t("history:added"));
                  } else {
                    msgList.push(
                      t("history:added_detail", {
                        field: fieldLabel || obj.name,
                        detail,
                      })
                    );
                  }
                  return;
                }

                // ---------- ADD DESTINATION PACKAGE ----------
                if (log.add_destination_package) {
                  const { username, container } = log.add_destination_package;
                  msgList.push(
                    t("history:add_destination_package", {
                      username: username ?? "",
                      container: container ?? "",
                    })
                  );
                  return;
                }

                // ---------- UPDATE DESTINATION PACKAGE ----------
                if (log.update_destination_package) {
                  const { username, container } = log.update_destination_package;
                  msgList.push(
                    t("history:update_destination_package", {
                      username: username ?? "",
                      container: container ?? "",
                    })
                  );
                  return;
                }

                // ---------- BILL ITEM REMOVED ----------
                if (log.bill_item_removed) {
                  const { username, reference, billNo } = log.bill_item_removed;
                  msgList.push(
                    t("history:bill_item_removed", {
                      username: username ?? "",
                      reference: reference ?? "",
                      billNo: billNo ?? "",
                    })
                  );
                  return;
                }

                // ---------- BILL ITEMS SAVED (POST /items) ----------
                if (log.bill_items_saved) {
                  const { username, reference, billNo } = log.bill_items_saved;
                  msgList.push(
                    t("history:bill_items_saved", {
                      username: username ?? "",
                      reference: reference ?? "",
                      billNo: billNo ?? "",
                    })
                  );
                  return;
                }

                // ---------- REMOVED / DELETED ----------
                if (log.removed || log.deleted) {
                  const obj = log.removed || log.deleted;
                  const fieldLabel = getFieldLabel(obj.name);
                  const detail = obj.object;

                  if (!fieldLabel && !detail) {
                    msgList.push(t("history:removed"));
                  } else {
                    msgList.push(
                      t("history:removed_detail", {
                        field: fieldLabel || obj.name,
                        detail,
                      })
                    );
                  }
                  return;
                }

                // ---------- CHANGED ----------
                if (log.changed) {
                  const obj = log.changed;
                  if (Array.isArray(obj.fields)) {
                    const fields = obj.fields.map((f) => getFieldLabel(f)).join(", ");

                    msgList.push(
                      t("history:changed_fields", {
                        fields,
                      })
                    );
                    return;
                  }
                  if (obj.name && obj.object) {
                    msgList.push(buildChangedFromToMsg(obj.object, obj.name));
                    return;
                  }
                  msgList.push(
                    t("history:changed_detail", {
                      field: getFieldLabel(obj.name),
                      detail: JSON.stringify(obj),
                    })
                  );
                  return;
                }
                msgList.push(JSON.stringify(log));
              });
            }
          }

          return {
            ...item,
            key: item.id, // ใช้ id จริงจะดีกว่า Math.random()
            username: item.app_user?.username,
            messageList: msgList,
          };
        });

        setTableData((prev) => ({
          ...prev,
          loading: false,
          dataSource,
          pagination: { ...prev.pagination, current: page, pageSize: limit, total: resp.totalCount },
        }));
      } catch (err) {
        console.error("[BtnHistory] searchHistory: ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
        setTableData(defaultTableData);
      }
    },
    [hasHistoryScope, company_bill_id, customer_bill_id, customer_id, object_id, contentTypeIds, t]
  );

  const onClick = useCallback(() => {
    setIsModalOpen(true);
    searchHistory({ page: 1, limit: 10 });
  }, [searchHistory]);

  const handleTableChange = useCallback(
    (current, pageSize) => {
      searchHistory({ page: current, limit: pageSize });
    },
    [searchHistory]
  );

  const onClose = useCallback(() => {
    setTableData(defaultTableData);
    setIsModalOpen(false);
  }, []);

  const tableProps = useMemo(
    () => ({
      ...tableData,
      pagination: {
        ...tableData.pagination,
        onChange: handleTableChange,
      },
    }),
    [tableData, handleTableChange]
  );

  return (
    <>
      {contextHolder}
      <Button type="default" icon={<HistoryOutlined />} onClick={onClick} disabled={!hasHistoryScope}>
        {t("BTN_History")}
      </Button>
      <ModalHistory title={title} open={isModalOpen} onClose={onClose} tableProps={tableProps} t={t} />
    </>
  );
};

export default BtnHistory;
